import React, { useState, useRef } from 'react';
import exifr from 'exifr';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../api/client';
import { invalidatePortal } from '../../hooks/usePortalQuery';
import CarProfileSelector from './CarProfileSelector';
import './Upload.css';

const Upload = () => {
  const { user } = useAuth();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const uploadBusy = useRef(false);
  const pendingConfirmations = useRef(new Map());
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadedFileHashes, setUploadedFileHashes] = useState(new Set());
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Create a unique hash for a file to identify duplicates
  const getFileHash = (file) => {
    return `${file.name}_${file.size}_${file.lastModified}`;
  };

  const handleFileSelect = async (files) => {
    if (uploadBusy.current || pendingConfirmations.current.size) return;
    const imageFiles = Array.from(files).filter(file => 
      file.type.startsWith('image/')
    );

    if (imageFiles.length === 0) {
      setError('Please select valid image files');
      return;
    }

    // Filter out already uploaded files
    const newImageFiles = imageFiles.filter(file => {
      const fileHash = getFileHash(file);
      return !uploadedFileHashes.has(fileHash);
    });

    const skippedCount = imageFiles.length - newImageFiles.length;
    const nonImageCount = files.length - imageFiles.length;

    // Build error/warning message
    let errorMessages = [];
    if (nonImageCount > 0) {
      errorMessages.push(`${nonImageCount} non-image file(s) skipped`);
    }
    if (skippedCount > 0) {
      errorMessages.push(`${skippedCount} already uploaded file(s) skipped`);
    }

    if (newImageFiles.length === 0) {
      if (skippedCount > 0) {
        setError('All selected files have already been uploaded');
      } else {
        setError('Please select valid image files');
      }
      return;
    }

    if (errorMessages.length > 0) {
      setError(errorMessages.join(', '));
    } else {
      setError(null);
    }

    // Extract metadata and shooting time for each file
    const filesWithPreview = await Promise.all(
      newImageFiles.map(async (file) => {
        const metadata = await extractFileMetadata(file);
        const shootingTime = metadata.shootingTime || file.lastModified || new Date(0).toISOString();
        
        return {
          file,
          preview: URL.createObjectURL(file),
          id: Math.random().toString(36).substr(2, 9),
          metadata,
          shootingTime,
          fileHash: getFileHash(file)
        };
      })
    );

    // Sort files by shooting time chronologically (oldest first)
    const sortedFiles = filesWithPreview.sort((a, b) => {
      const timeA = new Date(a.shootingTime).getTime();
      const timeB = new Date(b.shootingTime).getTime();
      return timeA - timeB;
    });

    // Merge with existing files and re-sort all files together
    setSelectedFiles(prev => {
      const allFiles = [...prev, ...sortedFiles];
      return allFiles.sort((a, b) => {
        const timeA = new Date(a.shootingTime || a.file.lastModified || 0).getTime();
        const timeB = new Date(b.shootingTime || b.file.lastModified || 0).getTime();
        return timeA - timeB;
      });
    });
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    handleFileSelect(files);
  };

  const handleRemoveFile = (fileId) => {
    if (uploadBusy.current || pendingConfirmations.current.has(fileId)) return;
    setSelectedFiles(prev => {
      const removed = prev.find(f => f.id === fileId);
      if (removed) {
        URL.revokeObjectURL(removed.preview);
      }
      return prev.filter(f => f.id !== fileId);
    });
    // Clear progress for removed file
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileId];
      return newProgress;
    });
  };

  // Extract metadata from file object including EXIF data
  const extractFileMetadata = async (file) => {
    const metadata = {
      filename: file.name,
      size: file.size,
      type: file.type,
    };

    try {
      // Extract EXIF data from image
      const exifData = await exifr.parse(file, {
        pick: ['DateTimeOriginal', 'CreateDate', 'ModifyDate', 'DateTime', 
               'Make', 'Model', 'GPSLatitude', 'GPSLongitude', 
               'Orientation', 'ExposureTime', 'FNumber', 'ISO']
      });

      if (exifData) {
        // Add shooting time (try multiple EXIF date fields in order of preference)
        const shootingTime = exifData.DateTimeOriginal || 
                            exifData.CreateDate || 
                            exifData.DateTime || 
                            exifData.ModifyDate;
        
        if (shootingTime) {
          metadata.shootingTime = new Date(shootingTime).toISOString();
        }

        // Add camera information
        if (exifData.Make) metadata.cameraMake = exifData.Make;
        if (exifData.Model) metadata.cameraModel = exifData.Model;

        // Add GPS location if available
        if (exifData.GPSLatitude && exifData.GPSLongitude) {
          metadata.gpsLatitude = exifData.GPSLatitude;
          metadata.gpsLongitude = exifData.GPSLongitude;
        }

        // Add other camera settings
        if (exifData.Orientation) metadata.orientation = exifData.Orientation;
        if (exifData.ExposureTime) metadata.exposureTime = exifData.ExposureTime;
        if (exifData.FNumber) metadata.fNumber = exifData.FNumber;
        if (exifData.ISO) metadata.iso = exifData.ISO;
      }
    } catch (error) {
      console.warn('Failed to extract EXIF data:', error);
      // Continue without EXIF data
    }

    return metadata;
  };

  const handleUpload = async () => {
    if (uploadBusy.current) return;
    if (selectedFiles.length === 0) {
      setError('Please select files to upload');
      return;
    }
    if (selectedFiles.length % 4 !== 0) {
      setError('Each reservation requires exactly 4 photos. Add or remove photos so every batch has 4.');
      return;
    }

    // Ensure user is logged in with valid username
    if (!user || !user.username) {
      setError('Please log in to upload files');
      return;
    }

    uploadBusy.current = true;
    setUploading(true);
    setError(null);

    const newUploadedFiles = [];
    const vehicleProfileId = selectedCar?.id;
    // Preserve the displayed order, including the fourth photo used for OCR.
    const sortedFiles = selectedFiles.slice();

    // Split sorted files into batches of 4
    const BATCH_SIZE = 4;
    const batches = [];
    for (let i = 0; i < sortedFiles.length; i += BATCH_SIZE) {
      batches.push(sortedFiles.slice(i, i + BATCH_SIZE));
    }

    // Process each batch sequentially
    for (const batch of batches) {
      try {
        const pending = pendingConfirmations.current.get(batch[0].id);
        if (pending && (pending.file_ids.length !== batch.length ||
          !pending.file_ids.every((id, index) => id === batch[index].id))) {
          throw new Error('This batch has an unverified confirmation. Keep its original four photos together before retrying.');
        }
        // Initialize progress for all files in batch
        batch.forEach((fileObj) => {
          setUploadProgress(prev => ({
            ...prev,
            [fileObj.id]: { status: 'uploading', progress: 0 }
          }));
        });

        // Prepare batch data using already extracted metadata
        const filesData = batch.map((fileObj) => ({
          file: fileObj.file,
          metadata: fileObj.metadata,
          onProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({
              ...prev,
              [fileObj.id]: { status: 'uploading', progress: percentCompleted }
            }));
          }
        }));

        // Upload the entire batch through apiClient (initiates, uploads to Azure, confirms)
        console.log('Uploading batch with uploader_id:', user?.username);
        const result = pending ? await apiClient.confirmPhotoUpload(pending.session_id, pending.vehicle_profile_id) :
          await apiClient.uploadPhotoBatch(filesData, user?.username, vehicleProfileId);

        // If we reach here, all uploads in the batch succeeded
        batch.forEach((fileObj) => {
          // Mark as complete
          setUploadProgress(prev => ({
            ...prev,
            [fileObj.id]: { status: 'success', progress: 100 }
          }));

          newUploadedFiles.push({
            ...fileObj,
            uploadedUrl: result.url || `Session: ${result.session_id}`,
            uploadedAt: new Date(),
            reservationId: result.reservation.id,
            vehicleProfileId: result.reservation.vehicle_id,
          });

          // Add to uploaded file hashes to prevent re-uploading
          setUploadedFileHashes(prev => new Set(prev).add(fileObj.fileHash || getFileHash(fileObj.file)));

          pendingConfirmations.current.delete(fileObj.id);
        });
        invalidatePortal();

      } catch (err) {
        console.error('Batch upload failed:', err);
        if (err.uploadSession) {
          const pending = { ...err.uploadSession, file_ids: batch.map((fileObj) => fileObj.id) };
          batch.forEach((fileObj) => pendingConfirmations.current.set(fileObj.id, pending));
          invalidatePortal();
        }
        // If any upload in the batch fails, mark all files in the batch as failed
        batch.forEach((fileObj) => {
          setUploadProgress(prev => ({
            ...prev,
            [fileObj.id]: { status: 'error', progress: 0, error: `Batch upload failed: ${err.message}${err.uploadSession ? ` Re-confirm session ${err.uploadSession.session_id}; do not start another upload for these photos.` : ''}` }
          }));
        });
        // Continue to next batch
      }
    }

    uploadBusy.current = false;
    setUploading(false);
    setUploadedFiles(prev => [...prev, ...newUploadedFiles]);
    
    // Clear successfully uploaded files
    const completed = new Set(newUploadedFiles.map((fileObj) => fileObj.id));
    setSelectedFiles(prev => prev.filter(fileObj => !completed.has(fileObj.id)));
  };

  const handleClearAll = () => {
    if (uploadBusy.current || pendingConfirmations.current.size) return;
    selectedFiles.forEach(f => URL.revokeObjectURL(f.preview));
    setSelectedFiles([]);
    setUploadProgress({});
    setError(null);
  };

  const handleClearHistory = () => {
    uploadedFiles.forEach(f => URL.revokeObjectURL(f.preview));
    setUploadedFiles([]);
    setUploadedFileHashes(new Set());
  };

  // Drag and drop handlers for reordering
  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnterItem = (e, index) => {
    e.preventDefault();
    if (draggedItem !== index) {
      setDragOverItem(index);
    }
  };

  const handleDragOverItem = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropItem = (e, dropIndex) => {
    e.preventDefault();
    if (uploadBusy.current || pendingConfirmations.current.size) return;
    
    if (draggedItem === null || draggedItem === dropIndex) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    const newFiles = [...selectedFiles];
    const draggedFile = newFiles[draggedItem];
    
    // Remove from old position
    newFiles.splice(draggedItem, 1);
    
    // Insert at new position
    newFiles.splice(dropIndex, 0, draggedFile);
    
    setSelectedFiles(newFiles);
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };

  return (
    <div className="upload-page">
      <div className="main-title">Upload Photos</div>
      
      <div className="upload-content">
        <CarProfileSelector selected={selectedCar} onChange={setSelectedCar}
          disabled={uploading || pendingConfirmations.current.size > 0} />
        {/* Upload Area */}
        <div 
          className={`upload-dropzone ${isDragging ? 'dragging' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className="upload-icon">📤</div>
          <div className="upload-text">
            <strong>Choose photos to upload</strong>
          </div>
          <div className="upload-hint">
            PNG, JPG, GIF up to 10MB each
          </div>
          
          {/* Upload Buttons */}
          <div className="upload-buttons">
            <button 
              className="upload-btn upload-btn-primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || pendingConfirmations.current.size > 0}
              type="button"
            >
              📁 Choose from Device
            </button>
            <button 
              className="upload-btn upload-btn-secondary"
              onClick={() => cameraInputRef.current?.click()}
              disabled={uploading || pendingConfirmations.current.size > 0}
              type="button"
            >
              📷 Take Photo
            </button>
          </div>
          
          <div className="upload-hint-desktop">
            Or drag and drop files here
          </div>
          
          {/* File input for gallery/files */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileInputChange}
            aria-label="Choose photo files"
            disabled={uploading || pendingConfirmations.current.size > 0}
            style={{ display: 'none' }}
          />
          
          {/* Camera input for mobile camera */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileInputChange}
            aria-label="Take a photo"
            disabled={uploading || pendingConfirmations.current.size > 0}
            style={{ display: 'none' }}
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="upload-error" role="alert">
            ⚠️ {error}
          </div>
        )}

        {/* Selected Files Preview */}
        {selectedFiles.length > 0 && (
          <div className="selected-files-section">
            <div className="section-header">
              <h3>Selected Files ({selectedFiles.length})</h3>
              <div className="section-actions">
                <button 
                  className="btn-secondary"
                  onClick={handleClearAll}
                  disabled={uploading || pendingConfirmations.current.size > 0}
                >
                  Clear All
                </button>
                <button 
                  className="btn-primary"
                  onClick={handleUpload}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload All'}
                </button>
              </div>
              <p>Use batches of exactly four photos. To retry a failed batch, keep its four photos together and click Upload All. Unverified confirmations reuse their original session and car selection.</p>
              {pendingConfirmations.current.size > 0 && <p role="alert">Some confirmations are unverified. Re-confirm the original sessions with Upload All before changing the car selection or photos.</p>}
            </div>

            <div className="files-container">
              {/* Group files into blocks of 4 */}
              {Array.from({ length: Math.ceil(selectedFiles.length / 4) }, (_, blockIndex) => {
                const startIndex = blockIndex * 4;
                const blockFiles = selectedFiles.slice(startIndex, startIndex + 4);
                
                return (
                  <div key={blockIndex} className="file-block-card">
                    <div className="files-grid">
                      {blockFiles.map((fileObj, fileIndex) => {
                        const globalIndex = startIndex + fileIndex;
                        return (
                          <div 
                            key={fileObj.id} 
                            className={`file-preview-card ${draggedItem === globalIndex ? 'dragging' : ''} ${dragOverItem === globalIndex ? 'drag-over' : ''}`}
                            draggable={!uploading && pendingConfirmations.current.size === 0}
                            onDragStart={(e) => handleDragStart(e, globalIndex)}
                            onDragEnter={(e) => handleDragEnterItem(e, globalIndex)}
                            onDragOver={handleDragOverItem}
                            onDrop={(e) => handleDropItem(e, globalIndex)}
                            onDragEnd={handleDragEnd}
                          >
                            <div className="file-preview-image">
                              <img src={fileObj.preview} alt={fileObj.file.name} />
                              {!uploading && pendingConfirmations.current.size === 0 && (
                                <button 
                                  className="remove-file-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveFile(fileObj.id);
                                  }}
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                            <div className="file-preview-info">
                              <div className="file-name">{fileObj.file.name}</div>
                              <div className="file-shooting-time">
                                {fileObj.shootingTime 
                                  ? new Date(fileObj.shootingTime).toLocaleString('en-US', {
                                      year: 'numeric',
                                      month: '2-digit',
                                      day: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      second: '2-digit',
                                      hour12: false
                                    })
                                  : 'No shooting time available'
                                }
                              </div>
                            </div>
                            
                            {/* Upload Progress */}
                            {uploadProgress[fileObj.id] && (
                              <div className="upload-progress-bar">
                                <div 
                                  className={`progress-fill ${uploadProgress[fileObj.id].status}`}
                                  style={{ 
                                    width: `${uploadProgress[fileObj.id].progress}%` 
                                  }}
                                />
                              </div>
                            )}
                            
                            {uploadProgress[fileObj.id]?.status === 'success' && (
                              <div className="upload-status success">✓ Uploaded</div>
                            )}
                            {uploadProgress[fileObj.id]?.status === 'error' && (
                              <div className="upload-status-error-container">
                                <div className="upload-status error">
                                  ✗ Failed
                                  {uploadProgress[fileObj.id]?.error && (
                                    <span className="error-message">: {uploadProgress[fileObj.id].error}</span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Uploaded Files History */}
        {uploadedFiles.length > 0 && (
          <div className="uploaded-files-section">
            <div className="section-header">
              <h3>Recently Uploaded ({uploadedFiles.length})</h3>
              <button 
                className="btn-secondary"
                onClick={handleClearHistory}
              >
                Clear History
              </button>
            </div>
            <div className="uploaded-files-list">
              {uploadedFiles.map((fileObj, index) => (
                <div key={index} className="uploaded-file-item">
                  <img src={fileObj.preview} alt={fileObj.file.name} className="uploaded-thumbnail" />
                  <div className="uploaded-file-details">
                    <div className="uploaded-file-name">{fileObj.file.name}</div>
                    <div className="uploaded-file-time">
                      {fileObj.uploadedAt.toLocaleString()}
                    </div>
                    <div>Car: {fileObj.vehicleProfileId} | Reservation: {fileObj.reservationId}</div>
                  </div>
                  <div className="uploaded-file-status">✓ Success</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Upload;